































































































































import {
  Vue,
  Component,
  Prop,
  Watch,
} from "@/shared/vuepack";
import DeductibleTrackerBarChart from "@/employee/components/deductibleTracker/DeductibleTrackerBarChart.vue";
import DeductibleTrackerFooter from "@/employee/components/deductibleTracker/DeductibleTrackerFooter.vue";
import {
  CardWidgetTab,
  HealthPlan,
  HealthPlanBenefit,
  PlanType
} from "@/shared/types";
import Button from "@/shared/components/ui/Button.vue";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import { useDeductibleTrackerStore } from "@/employee/store/deductibleTracker";
import { useClaimStore } from "@/employee/store/claim";
import { useUserPlanStore } from "@/employee/store/userPlan";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";

@Component({
  components: {
    DeductibleTrackerBarChart,
    DeductibleTrackerFooter,
    Button,
    GenericLoadingSpinner
  }
})
export default class DeductibleTrackerWidget extends Vue {

  deductibleTrackerStore = useDeductibleTrackerStore();
  claimStore = useClaimStore();
  userPlanStore = useUserPlanStore();
  custodianSettingsStore = useCustodianSettingsStore();

  @Prop()
  width: number;

  @Prop()
  height: number;

  selectedPlanName = "";
  selectedPlanScope = "";
  selectedPlanYear = 0;

  get canYearBack (): boolean {
    return !!this.deductibleTrackerStore.healthPlans.some(h => h.year < this.selectedPlanYear);
  }

  get canYearForward (): boolean {
    return !!this.deductibleTrackerStore.healthPlans.some(h => h.year > this.selectedPlanYear);
  }

  get hasYears (): boolean {
    return !!this.deductibleTrackerStore.healthPlans.some(h => h.year !== this.selectedPlanYear);
  }

  changeYear (targetYear: number) {
    const records = this.deductibleTrackerStore.healthPlans.find(h => h.year === targetYear);
    if (!records) return;
    this.selectedPlanYear = targetYear;
    this.selectedPlanName = this.deductibleTrackerStore.healthPlans.find(h => h.year === this.selectedPlanYear).name;
    this.setHealthPlanScope();
  }

  yearBack (): void {
    const years = this.deductibleTrackerStore.healthPlans
        .filter(h => h.year !== this.selectedPlanYear && h.year < this.selectedPlanYear)
        .map(h => h.year)
        .sort((a, b) => b - a);

    if (years.length === 0) return;

    this.changeYear(years[0]);
  }

  yearForward (): void {
    const years = this.deductibleTrackerStore.healthPlans
        .filter(h => h.year !== this.selectedPlanYear && h.year > this.selectedPlanYear)
        .map(h => h.year)
        .sort((a, b) => a - b);

    if (years.length === 0) return;

    this.changeYear(years[0]);
  }

  mounted (): void {
    this.deductibleTrackerStore.retrieveHealthPlanIsLinked();
    this.claimStore.loadUserHasThirdPartyClaims();
    if (this.deductibleTrackerStore.areHealthPlansLoaded) {
      this.setSelectedHealthPlan();
    }
  }

  @Watch("deductibleTrackerStore.areHealthPlansLoaded")
  setSelectedHealthPlan () {
    if (this.hasHealthPlans) {
      this.selectedPlanYear = Math.max(...this.deductibleTrackerStore.healthPlans.map(h => h.year));
      this.selectedPlanName = this.deductibleTrackerStore.healthPlans.filter(
          h => h.year === this.selectedPlanYear)[0].name;
      if (this.hasSelectedHealthPlan) {
        this.setHealthPlanScope();
      }
    }
  }

  @Watch("deductibleTrackerStore.isHealthPlanIsLinkedLoaded")
  loadPlans () {
    if (!this.deductibleTrackerStore.isHealthPlanIsLinkedLoaded) return;

    this.deductibleTrackerStore.loadHealthPlans();
  }

  handlePlanChange (value): void {
    this.selectedPlanName = value;
    this.setHealthPlanScope();
  }

  setHealthPlanScope () {
    if (this.hasMultiplePlanScopes) {
      this.selectedPlanScope = this.userPlanStore.planType === PlanType.Individual ? "individual" : "family";
    } else {
      this.selectedPlanScope = this.selectedHealthPlan.benefits.some(b => b.scope === "individual") ?
          "individual" : "family";
    }
  }

  updateSelectedPlanScope (value: string) {
    this.selectedPlanScope = value;
  }

  get hasMultiplePlanScopes () {
    return !!this.selectedHealthPlan.benefits.some(b => b.scope === "individual")
        && !!this.selectedHealthPlan.benefits.some(b => b.scope === "family");
  }

  get hasHealthPlans (): boolean {
    return this.deductibleTrackerStore.healthPlans !== undefined && this.deductibleTrackerStore.healthPlans.length !==
        0;
  }

  get hasSelectedHealthPlan (): boolean {
    return this.selectedHealthPlan !== undefined
        && this.selectedHealthPlan.benefits !== undefined
        && this.selectedHealthPlan.benefits.length !== 0;
  }

  get activeTabs (): CardWidgetTab[] {
    const tabs = [
      { title: "In-Network", disabled: this.inNetworkTabDisabled, visible: true },
      { title: "Out-Of-Network", disabled: this.outOfNetworkTabDisabled, visible: true }
    ];
    return tabs.filter(tab => tab.visible);
  }

  get selectedHealthPlan (): HealthPlan {
    return this.deductibleTrackerStore.healthPlans.find(
        p => p.name === this.selectedPlanName && p.year === this.selectedPlanYear);
  }

  get inNetworkTabDisabled (): boolean {
    return !this.selectedHealthPlan.benefits.some(b => b.network === "in" && b.scope === this.selectedPlanScope);
  }

  get outOfNetworkTabDisabled (): boolean {
    return !this.selectedHealthPlan.benefits.some(b => b.network === "out" && b.scope === this.selectedPlanScope);
  }

  get inNetworkDeductibleEnabled (): boolean {
    return this.selectedHealthPlan.benefits.some(b => b.network === "in"
        && b.type === "deductible"
        && b.scope === this.selectedPlanScope);
  }

  get inNetworkOutOfPocketMaxEnabled (): boolean {
    return this.selectedHealthPlan.benefits.some(b => b.network === "in"
        && b.type === "oop-max"
        && b.scope === this.selectedPlanScope);
  }

  get outOfNetworkDeductibleEnabled (): boolean {
    return this.selectedHealthPlan.benefits.some(b => b.network === "out"
        && b.type === "deductible"
        && b.scope === this.selectedPlanScope);
  }

  get outOfNetworkOutOfPocketMaxEnabled (): boolean {
    return this.selectedHealthPlan.benefits.some(b => b.network === "out"
        && b.type === "oop-max"
        && b.scope === this.selectedPlanScope);
  }

  get outOfNetworkBenefit (): HealthPlanBenefit {
    return this.selectedHealthPlan.benefits.find(b => b.network === "out" && b.scope === this.selectedPlanScope);
  }

  get inBenefit (): HealthPlanBenefit {
    return this.selectedHealthPlan.benefits.find(b => b.network === "in"
        && b.scope === this.selectedPlanScope
        && b.type === "deductible");
  }

  get inNetworkDeductibleCurrent (): number {
    return this.inBenefit.amount;
  }

  get inNetworkDeductibleMax (): number {
    return this.inBenefit.max;
  }

  get inOopBenefit (): HealthPlanBenefit {
    return this.selectedHealthPlan.benefits.find(b => b.network === "in"
        && b.scope === this.selectedPlanScope
        && b.type === "oop-max");
  }

  get inNetworkOutOfPocket (): number {
    return this.inOopBenefit.amount;
  }

  get inNetworkOutOfPocketMax (): number {
    return this.inOopBenefit.max;
  }

  get outDeductible (): HealthPlanBenefit {
    return this.selectedHealthPlan.benefits.find(b => b.network === "out"
        && b.scope === this.selectedPlanScope
        && b.type === "deductible");
  }

  get outOfNetworkDeductibleCurrent (): number {
    return this.outDeductible.amount;
  }

  get outOfNetworkDeductibleMax (): number {
    return this.outDeductible.max;
  }

  get outOop (): HealthPlanBenefit {
    return this.selectedHealthPlan.benefits.find(b => b.network === "out"
        && b.scope === this.selectedPlanScope
        && b.type === "oop-max");
  }

  get outOfNetworkOutOfPocket (): number {
    return this.outOop.amount;
  }

  get outOfNetworkOutOfPocketMax (): number {
    return this.outOop.max;
  }

  get healthPlanOptions (): string[] {
    return this.deductibleTrackerStore.healthPlans.filter(h => h.year === this.selectedPlanYear).map(p => p.name);
  }

  get signInHealthPlanText (): string {
    return `Sign into your healthcare insurance provider and see how much you have paid towards your deductible${this.custodianSettingsStore.isHsaBankCustodian
        ? "" : " and to view your claims"}.`;
  }

  get busySettingUpData (): string {
    return `Thanks for connecting your health plan. We are busy working on getting your data; this could take 1-2 days. Check back to see your deductible ${this.custodianSettingsStore.isHsaBankCustodian
        ? "information" : "and claims information"}.`;
  }
}
